import React, { useEffect, useRef } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";

/**
 * A right-hand detail drawer. Non-modal (the table stays usable behind it, so clicking
 * another row just swaps the content); Escape or the close button dismisses it.
 */
interface Props {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  onClose: () => void;
  children: React.ReactNode;
  footer?: React.ReactNode;
}

const SidePanel: React.FC<Props> = ({ title, subtitle, onClose, children, footer }) => {
  const ref = useRef<HTMLElement>(null);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // Let an open menu / dialog consume its own Escape first.
      if (e.key === "Escape" && !e.defaultPrevented) onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <aside
      ref={ref}
      aria-label="Station details"
      className="fixed inset-y-0 right-0 z-40 flex w-full max-w-md flex-col border-l border-gray-700 bg-gray-900 shadow-2xl"
    >
      <div className="flex items-start justify-between gap-3 border-b border-gray-700 px-5 py-4">
        <div className="min-w-0">
          <h2 className="truncate text-lg font-semibold text-white">{title}</h2>
          {subtitle && <div className="mt-0.5 break-all font-mono text-xs text-gray-400">{subtitle}</div>}
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close details"
          className="rounded p-1 text-gray-400 hover:bg-gray-800 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500"
        >
          <XMarkIcon className="h-5 w-5" aria-hidden="true" />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>
      {footer && <div className="border-t border-gray-700 px-5 py-3">{footer}</div>}
    </aside>
  );
};

/** One label / value row for a detail panel. */
export const DetailRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="flex justify-between gap-4 py-1.5 text-sm">
    <dt className="shrink-0 text-gray-400">{label}</dt>
    <dd className="min-w-0 break-words text-right text-white tabular-nums">{children}</dd>
  </div>
);

/** A titled group inside a detail panel. */
export const DetailSection: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <section className="mb-5">
    <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-300">{title}</h3>
    <dl className="divide-y divide-gray-800">{children}</dl>
  </section>
);

export default SidePanel;
